/**
 * useResetStaffPassword — TanStack mutation over
 * `POST /admin/users/:userId/reset-password` (no body).
 *
 * Platform admin-initiated password-reset trigger. Available to root +
 * support (`staff:reset_password` maps to both roles — see
 * `shared/auth/admin-permissions.ts`). Success is 204 No Content.
 *
 * Unlike the other staff mutations this surfaces a structured RESULT
 * rather than throwing on the non-success branches, because every
 * outcome (notFound / rateLimited) is a UI state the caller renders
 * inline (toast / disabled-with-countdown) rather than a thrown error.
 *
 * Result union:
 *   - 'success'     — 204.
 *   - 'notFound'    — 404 (anti-enum; no such user).
 *   - 'forbidden'   — 403; a support actor may not reset a ROOT member's
 *                     password (support→root guardrail). Distinct from the
 *                     generic error so the UI can render a permission message.
 *   - 'rateLimited' — 429; carries `retryAfter` seconds parsed from the
 *                     `Retry-After` header (null when absent / unparseable).
 *   - 'error'       — anything else.
 *
 * Does NOT invalidate the staff list — a password-reset trigger does
 * not mutate any staff-list field.
 */

import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';

export type ResetStaffPasswordResult =
  | { readonly kind: 'success' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'rateLimited'; readonly retryAfter: number | null }
  | { readonly kind: 'error' };

export interface UseResetStaffPasswordResult {
  readonly mutateAsync: (userId: string) => Promise<ResetStaffPasswordResult>;
  readonly isPending: boolean;
}

/** Parse a `Retry-After` header (seconds form) into a positive integer. */
function parseRetryAfter(headerValue: string | null): number | null {
  if (headerValue === null) {
    return null;
  }
  const seconds = Number.parseInt(headerValue, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

export function useResetStaffPassword(): UseResetStaffPasswordResult {
  const mutation = useMutation<ResetStaffPasswordResult, never, string>({
    mutationFn: async (userId) => {
      const { response } = await apiClient.POST(
        '/admin/users/{userId}/reset-password',
        { params: { path: { userId } } },
      );
      if (response.ok) {
        return { kind: 'success' };
      }
      if (response.status === 404) {
        return { kind: 'notFound' };
      }
      if (response.status === 403) {
        return { kind: 'forbidden' };
      }
      if (response.status === 429) {
        return {
          kind: 'rateLimited',
          retryAfter: parseRetryAfter(response.headers.get('Retry-After')),
        };
      }
      return { kind: 'error' };
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
